<template>
  <component
    :is="href ? 'a' : 'button'"
    name="rb-button"
    :form="props.form"
    :type="props.type"
    :style="cssVars"
    :href="props.href"
    :target="props.target"
    :class="{ primary, secondary, rounded, plain }"
    @click="$emit('click', $event)"
  >
    {{ props.text }}
  </component>
</template>

<script lang="ts" setup>
import { computed, withDefaults } from 'vue';
import { getTextColor } from '@/lib/Utils';

defineEmits(['click']);

const props = withDefaults(defineProps<{
  text: string,
  color?: string,
  textColor?: string,
  type?: 'button' | 'submit' | 'reset'
  form?: string,
  primary?: boolean,
  secondary?: boolean,
  rounded?: boolean,
  plain?: boolean,
  href?: string,
  target?: string
}>(), {
  type: 'button',
  color: '#FFFFFF',
  textColor: undefined,
  form: undefined,
  href: undefined,
  target: undefined
});

const cssVars = computed(() => {
  return {
    ['--color']: props.color,
    ['--text']: props.textColor ?? getTextColor(props.color)
  };
});
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

button, a {
  cursor: pointer;
  position: relative;
  height: 36px;
  max-width: 100%;
  padding: 0 16px;

  outline: none;
  border: none;
  border-radius: 4px;

  justify-content: center;
  vertical-align: middle;
  align-items: center;

  background-color: var(--color);

  color: var(--text);

  &.rounded {
    border-radius: 50px;
  }

  &.primary {
    background-color: var(--primary);
    color: var(--contrast-text-primary);
  }

  &.secondary {
    background-color: var(--secondary);
    color: var(--contrast-text-secondary);
  }

  &.plain {
    box-shadow: unset;
    padding: 0;
    background: none;

    &:hover {
      box-shadow: unset;
    }
  }

  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.045rem;
  line-height: 1rem;

  box-shadow: 0px 3px 1px -2px var(--shadow-2),
  0px 2px 2px 0px var(--shadow-1-4),
  0px 1px 5px 0px var(--shadow-1-2);

  &:hover {
    box-shadow: 0px 3px 1px -2px var(--shadow-2), 0px 4px 5px 0px var(--shadow-1-4), 0px 1px 10px 0px var(--shadow-1-2);
  }
}
</style>
